<template>
  <div class="upload-image">
    <a-upload :action="toUrl('/v1/api/directAttachment/material')"
              :show-upload-list="false"
              :with-credentials="true"
              accept="image/png, image/jpeg"
              :file-list="fileList"
              :before-upload="verifyUploadImgName"
              @change="handleChange">
      <a-button class="upload-button"
                @click="toLogin($event)">
        <plus-outlined class="upload-button-icon" />
        <span class="upload-button-title">{{ $t('CustomSpus.UpLoadImage.uploadImage') }}</span>
      </a-button>
    </a-upload>
    <div class="hint">
      <div class="hint-text">
        <p>-&nbsp</p><span>{{ $t('CustomSpus.UpLoadImage.hint1') }}</span>
      </div>
      <div class="hint-text">
        <p>-&nbsp</p><span>{{ $t('CustomSpus.UpLoadImage.hint2') }}</span>
      </div>
      <div class="hint-text">
        <p>-&nbsp</p><span>{{ $t('CustomSpus.UpLoadImage.hint3') }}</span>
      </div>
    </div>
    <div class="horizontal-bar"></div>
    <div class="upload-title">{{ $t('CustomSpus.UpLoadImage.recentUploadRecords') }}</div>
    <div class="material-content">
      <ul class="material-list">
        <li v-for="(item, index) in materialItems"
            :key="index"
            class="material-item"
            @click="addImage(item.image)">
          <a-popover trigger="hover"
                     placement="right"
                     :get-popup-container="getPopupContainer">
            <template #content>
              <div class="image-content">
                <img class="image-content-img"
                     :src="toUrl(item.image) + '?x-oss-process=image/resize,w_50/quality,q_90'" />
                <div class="image-content-name">{{ item.name }}</div>
                <div class="image-content-tagbox">
                  <div v-for="(tag, tagIndex) in item.tag?.split(',')"
                       :key="tagIndex"
                       class="image-content-tag">
                    <span v-for="tagItem in tagList"
                          :key="tagItem.id">
                      <span v-if="tag == tagItem.key">{{ tagItem.name }}</span>
                    </span>
                  </div>
                </div>
              </div>
            </template>
            <img class="material-img"
                 :src="toUrl(item.image) + '?x-oss-process=image/resize,w_200/quality,q_90'" />
          </a-popover>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { message, type UploadProps } from 'ant-design-vue'
import type { UploadChangeParam } from 'ant-design-vue/es/upload/interface'
import { addMateriaImage, getUserMaterialList } from '../../api'
import { fetchMaterialTag } from '../../api/index'
import { toUrl, verifyUploadImgName } from "@/utils"
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
const props = defineProps<{
  isLogin: any
}>()
const emit = defineEmits([
  'changes',
  'toLogin'
])

onMounted(() => {
  getUserMaterialList({}).then((result: any) => {
    materialItems.value = result.rows
  })
  fetchMaterialTag().then(result => {
    tagList.value = result.data.items
  })
})
const getPopupContainer = (triggerNode: any) => {
  return triggerNode.parentNode
}
const tagList = ref<any[]>([])
const fileList = ref<UploadProps['fileList']>([])
const materialItems = ref<any>([])
const toLogin = (event: any) => {
  if (!props.isLogin) {
    event.stopPropagation()
    emit('toLogin')
  }
}
const handleChange = async (info: UploadChangeParam) => {
  fileList.value = []
  fileList.value.push(info.file)
  if (info.file.status === 'done') {
    const data = {
      image: '',
      name: '',
      specifications: ''
    }
    data.image = info.file.response.uploaded
    data.name = info.file.name
    const img = new Image()
    img.src = toUrl(info.file.response.uploaded) as string
    img.onload = async () => {
      const size = info.file.size ? (info.file.size / 1024).toFixed(2) : ''
      data.specifications = `t('CustomSpus.UpLoadImage.width')：${img.width}x${img.height} t('CustomSpus.UpLoadImage.type')：${info.file.type} t('CustomSpus.UpLoadImage.size')：${size}KB`
      await addMateriaImage(data)
      await getUserMaterialList({}).then((result: any) => {
        materialItems.value = result.rows
      })
    }
    emit('changes', toUrl(data.image))
  }
  if (info.file.status === 'error') {
    message.error(t('CustomSpus.UpLoadImage.uploadFailed'))
    fileList.value = []
  }
}
const addImage = (imageUrl: string) => {
  const imageTransformUrl = toUrl(imageUrl)
  emit('changes', imageTransformUrl)
}

</script>
<style scoped lang="less">
.upload-image {
  width: 300px;
  flex: 1;
  display: flex;
  flex-flow: column;
  background: white;
  padding: 20px;
  overflow: hidden;

  :deep(.ant-popover-inner-content) {
    padding: 0 !important;
  }

  .horizontal-bar {
    width: 255px;
    border-bottom: 1px solid #F6F6F6;
    margin: 20px;
    margin-left: 0;
  }

  .upload-title {
    margin-bottom: 10px;
  }

  .hint {
    margin-top: 20px;

    .hint-text {
      display: flex;

      span {
        font-size: 14px;
        font-weight: 400;
        color: #999999;
      }
    }
  }

  p {
    color: #111111ad;
  }

  .upload-button {
    margin-top: 20px;
    border: none;
    width: 260px;
    height: 46px;
    background: #F6F6F6;
    display: flex;
    align-items: center;
    justify-content: center;

    .upload-button-icon {
      font-size: 16px;
    }

    .upload-button-title {
      font-size: 14px;
      font-weight: 400;
      color: #111111;
    }
  }

  .material-content {
    overflow: auto;
    flex: 1;
    scrollbar-width: thin;
    /* Firefox */
    scrollbar-color: transparent transparent;

    /* Firefox */
    .material-list {
      display: flex;
      justify-content: space-around;
      flex-wrap: wrap;
      width: 100%;

      .material-item {
        background-color: #F6F6F6;
        width: 70px;
        height: 70px;
        margin-top: 5px;
        margin-bottom: 5px;
        margin-left: 5px;
        cursor: pointer;

        .material-img {
          max-width: 100%;
          max-height: 100%;
        }
      }

      .material-item:nth-child(3n) {
        margin-right: 0px !important;
      }
    }
  }

  .material-content::-webkit-scrollbar {
    width: 0;
  }
}


.image-content {
  width: 255px;
  height: 100%;
  padding-bottom: 10px;

  .image-content-img {
    width: 255px;
    height: 256px;
    background: #f6f6f6;
  }

  .image-content-name {
    margin: 10px 20px;
    word-wrap: break-word;
  }

  .image-content-tagbox {
    display: flex;
    flex-wrap: wrap;
    margin-left: 20px;

    .image-content-tag {
      min-width: 68px;
      height: 24px;
      line-height: 24px;
      background: #F6F6F6;
      padding: 0 5px;
      font-size: 14px;
      color: #999;
      text-align: center;
      margin: 0 6px 6px 0;
    }
  }
}
</style>
